import React, { useRef, useState } from 'react';
import { FaEnvelope, FaMapMarkerAlt, FaPhone } from 'react-icons/fa';
import GoogleMapReact from 'google-map-react';
import { Paper } from '../components';
import { apiKey } from '../firebase';
import {
  readStorage,
  removeStorage,
  updateStorage,
} from 'utils/storageHandler';

const officeLocation1 = {
  lat: 52.833647,
  lng: 13.806132,
};

const officeLocation2 = {
  lat: 52.838678,
  lng: 13.73733,
};

const MAP_CONSENT_KEY = 'google-maps:v1';
const CONSENT_SCOPE = 'consent';

const readMapConsent = () => {
  try {
    return readStorage(CONSENT_SCOPE)[MAP_CONSENT_KEY] === 'granted';
  } catch {
    return false;
  }
};

const Marker = ({ lat, lng }: { lat: number; lng: number }) => (
  <div className="text-center">
    <FaMapMarkerAlt className="text-red text-4xl absolute -top-10" />
  </div>
);

const Contact = () => {
  const [mapEnabled, setMapEnabled] = useState(readMapConsent);

  const enableMap = () => {
    try {
      updateStorage({ [MAP_CONSENT_KEY]: 'granted' }, CONSENT_SCOPE);
    } catch {
      // If storage is unavailable, consent applies only to this page load.
    }
    setMapEnabled(true);
  };

  const withdrawMapConsent = () => {
    try {
      removeStorage(MAP_CONSENT_KEY, CONSENT_SCOPE);
    } catch {
      // Storage may be disabled by the browser.
    }
    setMapEnabled(false);
    window.location.reload();
  };
  const [selectedOffice, setSelectedOffice] = useState('eberswalde');
  const [viewport, setViewport] = useState({
    center: officeLocation1,
    zoom: 16,
  });
  const mapContainer = useRef<HTMLDivElement>(null);

  const selectOffice = (office: 'eberswalde' | 'finow') => {
    setSelectedOffice(office);
    setViewport({
      center: office === 'eberswalde' ? officeLocation1 : officeLocation2,
      zoom: 16,
    });
    mapContainer.current?.scrollIntoView?.({ block: 'nearest' });
  };

  return (
    <div className="flex flex-col w-full h-full overflow-y-auto bg-black text-white lg:text-lg lg:landscape:flex-row">
      <div
        ref={mapContainer}
        className="relative flex-[1_0_22rem] lg:landscape:flex-1 lg:landscape:min-w-0 lg:landscape:h-full"
      >
        {mapEnabled ? (
          <>
            <button
              type="button"
              className="py-3 px-4 border border-red bg-black text-red hover:bg-red hover:text-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-[3px] absolute bottom-8 left-4 z-[1] max-w-[calc(100%-2rem)]"
              onClick={withdrawMapConsent}
            >
              Einwilligung widerrufen und Seite neu laden
            </button>
            <GoogleMapReact
              // @ts-ignore
              options={
                {
                  fullscreenControlOptions: { position: 1 },
                  cameraControlOptions: { position: 1 },
                } as any
              }
              bootstrapURLKeys={{ key: apiKey || '' }}
              center={viewport.center}
              zoom={viewport.zoom}
              onChange={({ center, zoom }) => setViewport({ center, zoom })}
            >
              <Marker lat={officeLocation1.lat} lng={officeLocation1.lng} />
              <Marker lat={officeLocation2.lat} lng={officeLocation2.lng} />
            </GoogleMapReact>
          </>
        ) : (
          <section
            className="flex flex-col items-center justify-center gap-4 min-h-full p-6 text-center"
            aria-labelledby="map-consent-title"
          >
            <h2 id="map-consent-title" className="text-red text-xl lg:text-2xl">
              Google Maps ist deaktiviert
            </h2>
            <p>Erst mit deiner Einwilligung laden wir die Karte von Google.</p>
            <p>
              Wir merken uns deine Einwilligung in diesem Browser, bis du sie
              widerrufst oder die Website-Daten löschst.
            </p>
            <a
              className="underline"
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Datenschutzhinweise von Google
            </a>
            <button
              type="button"
              className="py-3 px-4 border border-red bg-black text-red hover:bg-red hover:text-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-[3px]"
              onClick={enableMap}
            >
              Einwilligen und Google Maps laden
            </button>
          </section>
        )}
      </div>

      <Paper className="w-full sm:px-10 lg:landscape:w-1/4 lg:landscape:px-2 lg:landscape:shrink-0">
        <div className="grid w-full text-center lg:text-left grid-cols-1 gap-4 px-2 py-3 lg:gap-8 lg:px-4 lg:py-6">
          <section aria-labelledby="offices-heading" className="min-w-0 mb-2">
            <h2
              id="offices-heading"
              className="text-yellow text-xl lg:text-2xl mb-2 lg:mb-4"
            >
              Fillialen
            </h2>
            <div className="flex items-start justify-center lg:justify-start mb-2 lg:mb-4">
              <div>
                <h3 className="text-yellow lg:mb-1">
                  <button
                    type="button"
                    className="uppercase underline underline-offset-4 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                    aria-pressed={selectedOffice === 'eberswalde'}
                    onClick={() => selectOffice('eberswalde')}
                  >
                    Eberswalde
                  </button>
                </h3>
                <address className="not-italic leading-normal lg:leading-relaxed">
                  Eisenbahnstraße 31
                  <br />
                  16225 Eberswalde
                </address>
              </div>
            </div>
            <div className="flex items-start justify-center lg:justify-start">
              <div>
                <h3 className="text-yellow lg:mb-1">
                  <button
                    type="button"
                    className="uppercase underline underline-offset-4 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                    aria-pressed={selectedOffice === 'finow'}
                    onClick={() => selectOffice('finow')}
                  >
                    Finow
                  </button>
                </h3>
                <address className="not-italic leading-normal lg:leading-relaxed">
                  Eberswalder Straße 63
                  <br />
                  16227 Eberswalde
                </address>
              </div>
            </div>
          </section>
          <section aria-labelledby="contact-heading" className="min-w-0">
            <h2
              id="contact-heading"
              className="text-yellow text-xl lg:text-2xl mb-2 lg:mb-4"
            >
              Kontaktmöglichkeiten
            </h2>
            <div className="flex items-start justify-center lg:justify-start mb-2 lg:mb-4">
              <div>
                <h3 className="flex items-center justify-center gap-2 text-yellow lg:justify-start lg:mb-1">
                  <FaPhone
                    className="shrink-0"
                    aria-hidden="true"
                    focusable="false"
                  />
                  Fahrlehrer
                </h3>
                <a className="hover:text-yellow" href="tel:+491727316142">
                  0172 7316142
                </a>
              </div>
            </div>
            <div className="flex items-start justify-center lg:justify-start mb-2 lg:mb-4">
              <div>
                <h3 className="flex items-center justify-center gap-2 text-yellow lg:justify-start lg:mb-1">
                  <FaPhone
                    className="shrink-0"
                    aria-hidden="true"
                    focusable="false"
                  />
                  Büro
                </h3>
                <a className="hover:text-yellow" href="tel:+493334385256">
                  03334 385256
                </a>
              </div>
            </div>
            <div className="flex items-start justify-center lg:justify-start">
              <div className="min-w-0">
                <h3 className="flex items-center justify-center gap-2 text-yellow lg:justify-start lg:mb-1">
                  <FaEnvelope
                    className="shrink-0"
                    aria-hidden="true"
                    focusable="false"
                  />
                  E-Mail
                </h3>
                <a
                  className="break-words hover:text-yellow"
                  href="mailto:kontakt@unternbuchen.de"
                >
                  kontakt@unternbuchen.de
                </a>
              </div>
            </div>
          </section>
        </div>
      </Paper>
    </div>
  );
};

export default Contact;
